import { useEffect, useState, type ReactNode } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { api, modesUrl } from '../lib/api';
import type { RosterMember } from '../lib/roster';
import { TourProvider, useTour } from '../tour/TourContext';

/* Chrome for the signed-in side only. The play surface never renders inside this — no
   sidebar, no header, no toast may appear in a recording. */

interface Props {
  member: RosterMember;
  onSignOut: () => void;
  children: ReactNode;
  narrow?: boolean;
}

export default function AppShell(props: Props) {
  /* The provider wraps the shell so a "?" anywhere inside — down in an item card — can open the
     tour without a callback threaded through every component between. The tour itself lives at
     this level so it survives the route changes it makes: it walks from the series list into a
     real series and on through both banks. */
  return (
    <TourProvider>
      <Shell {...props} />
    </TourProvider>
  );
}

function Shell({ member, onSignOut, children, narrow = true }: Props) {
  const { open: openTour } = useTour();
  const location = useLocation();
  const navigate = useNavigate();

  /* `?tour=readiness` opens the tour at that step — the shareable half of the "?" buttons, so an
     explanation can be pasted into Slack rather than described.
   *
   * ⚠️ The parameter is stripped immediately, with replace so it leaves no history entry. A
   * query param that survives is the classic failure here: the tour reopens on every refresh and
   * on Back, and there is no way to dismiss it for good. */
  useEffect(() => {
    const at = new URLSearchParams(location.search).get('tour');
    if (!at) return;
    openTour(at);
    const rest = new URLSearchParams(location.search);
    rest.delete('tour');
    const q = rest.toString();
    navigate({ pathname: location.pathname, search: q ? `?${q}` : '' }, { replace: true });
  }, [location.pathname, location.search, navigate, openTour]);

  /* What each tab holds. Read once on mount rather than polled: these are a glance, not a live
     counter, and polling on every internal page would be a request a minute forever. Fetched
     together so one slow call does not leave the bar half-numbered. */
  const [counts, setCounts] = useState<{ series: number; endless: number; fake: number; intake: number }>(
    { series: 0, endless: 0, fake: 0, intake: 0 },
  );
  useEffect(() => {
    let cancelled = false;
    void Promise.all([
      /* ACTIVE series only — the same rule the Series page's own tabs use. Archiving is how a
         finished series gets out of the way, so counting archived ones would mean the number
         never falls and tidying up appears to achieve nothing. */
      api.listRounds().then((rs) => rs.filter((r) => r.status !== 'archived').length).catch(() => 0),
      api.endless().then((r) => r.round.items.length).catch(() => 0),
      api.fake().then((r) => r.round.items.length).catch(() => 0),
      api.intake().then((r) => r.round.items.length).catch(() => 0),
    ]).then(([series, endless, fake, intake]) => {
      // The tabs still work without their counts; they just show none.
      if (!cancelled) setCounts({ series, endless, fake, intake });
    });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="app-shell">
      <header className="app-bar">
        <div className="brand">
          <Link to="/" style={{ textDecoration: 'none' }}>
            <span className="wordmark">Adorian</span>
          </Link>
          <span className="product">Real or Fake</span>
        </div>
        {/* The places headlines live. They are separate on purpose: a headline sits in exactly
            one of them, so one in a series never appears in a bank, and vice versa.
            The two banks split apart 2026-08-21 — real headlines are found, fake ones are written
            and then need a graphic drawn, and in one bucket the artwork backlog was invisible. */}
        {/* Intake first, because it is the only tab with work waiting in it: candidates arrive
            on a schedule whether or not anyone asked, and a queue nobody sees is a queue nobody
            empties. Series and the pools are places you go on purpose.
            Every tab carries its size — Series counts ACTIVE only, so archiving something makes
            the number fall. */}
        <nav className="app-nav">
          <NavLink to="/intake" className={({ isActive }) => (isActive ? 'on' : '')}>
            Intake{counts.intake > 0 && <span className="nav-count">{counts.intake}</span>}
          </NavLink>
          <NavLink to="/" end className={({ isActive }) => (isActive ? 'on' : '')}>
            Series{counts.series > 0 && <span className="nav-count">{counts.series}</span>}
          </NavLink>
          <NavLink to="/endless" className={({ isActive }) => (isActive ? 'on' : '')}>
            Real Headlines{counts.endless > 0 && <span className="nav-count">{counts.endless}</span>}
          </NavLink>
          <NavLink to="/fake" className={({ isActive }) => (isActive ? 'on' : '')}>
            Fake Headlines{counts.fake > 0 && <span className="nav-count">{counts.fake}</span>}
          </NavLink>
        </nav>
        <div className="who">
          {/* Onboarding, for a new member of the team. Left of Adorian's View and present on
              every internal page, because the tour walks across several of them. */}
          <button className="btn nav-action" onClick={() => openTour()} data-testid="take-tour">
            Take Tour
          </button>
          {/* Adorian's front door, from inside the builder.
              A real <a> to the URL rather than a router Link, and target=_blank: /modes renders
              OUTSIDE this shell, so navigating in place would replace the page you are working
              on. New tab means you can flip to his view and back without losing an edit. */}
          <a className="btn nav-action" href={modesUrl()} target="_blank" rel="noreferrer" title="Opens Adorian's Modes screen in a new tab">
            Adorian&rsquo;s View
          </a>
          {/* The browser extension's setup page. Up here because it is the one thing in this app
              you go looking for while you are somewhere ELSE — mid-research, on an article — and
              /connect was previously reachable only from a link somebody had to have been sent.
              It is also where the update banner sends people, so it needs a permanent address in
              the UI rather than a one-time URL.
              A router Link, not an <a>: /connect renders inside this shell, so it should behave
              like the rest of the builder and keep the tab. */}
          <Link className="btn nav-action" to="/connect" title="Capture headlines from the article you are reading">
            Extension
          </Link>
          <span>
            Signed in as <strong>{member.name}</strong>
          </span>
          <button className="btn ghost" onClick={onSignOut}>
            Sign Out
          </button>
        </div>
      </header>
      <main className={`app-main${narrow ? ' narrow' : ''}`}>{children}</main>
    </div>
  );
}
